import { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import EnvironmentGuard from 'src/components/EnvironmentGuard';
import ViewByToggle from 'src/components/ViewByToggle';
import { hasFilterParams, readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { NO_GROUP, groupFilterFrom } from './groupFilterUtils';
import UnmatchedFilesCharts from './UnmatchedFilesCharts';
import UnmatchedFilesTable from './UnmatchedFilesTable';
import UnmatchedCodeSignersTable from './UnmatchedCodeSignersTable';
import UnmatchedMatchedAppsTable from './UnmatchedMatchedAppsTable';
import {
  UNMATCHED_FILE_LAST_SEEN_RANGE,
  UNMATCHED_FILE_TRUST_SIGNAL,
  DEFAULT_UNMATCHED_FILE_GROUP_BY,
} from './constants';
import { UNMATCHED_FILE_STATUS } from 'src/utils/enums/unmatchedFileStatus';

const DEFAULT_FILTERS = {
  tagId: '',
  trustStatus: UNMATCHED_FILE_STATUS.Unknown,
  trustSignal: UNMATCHED_FILE_TRUST_SIGNAL.All,
  lastSeenRange: UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days,
};

// Which table is showing is as much a part of this view as the filters are, and the signers table
// treats its grouping as one of its own filters, so both belong in the URL next to them.
const VIEW_OPTIONS = [
  { value: 'files', label: 'Files', icon: 'mdi:file-document-multiple-outline' },
  { value: 'signers', label: 'Signers', icon: 'mdi:certificate-outline' },
  { value: 'matchedApps', label: 'Apps', icon: 'mdi:application-cog-outline' },
];
const TABLES = VIEW_OPTIONS.map(option => option.value);
const DEFAULT_VIEW = { view: TABLES[0], groupBy: DEFAULT_UNMATCHED_FILE_GROUP_BY };

const UnmatchedFiles = () => {
  const initialView = useMemo(() => {
    const stored = readFilterParams(DEFAULT_VIEW) ?? DEFAULT_VIEW;
    return TABLES.includes(stored.view) ? stored : { ...stored, view: DEFAULT_VIEW.view };
  }, []);

  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILTERS) ?? DEFAULT_FILTERS,
  );
  // Search reads as one more filter on the view, so it survives switching tables the way the rest
  // of them do. It stays out of the URL because it is free text rather than a shareable selection.
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState(initialView.groupBy);
  const [activeTable, setActiveTable] = useState(initialView.view);
  // Arriving with filters already in the URL means the page is being returned to, so the starter
  // focus is skipped rather than overwriting them.
  const [tableQueriesEnabled, setTableQueriesEnabled] = useState(() =>
    hasFilterParams(DEFAULT_FILTERS),
  );
  const [group, setGroup] = useState(() => readFilterParams(NO_GROUP) ?? NO_GROUP);

  // Switching tables replaces the action bar, so the bar cannot be what records a change that
  // switched them — the instance that would write it is the one being unmounted. The page owns the
  // whole view instead: which table is showing, how it is grouped, and the group drilled into. Kept
  // apart from the filters so clearing every filter still reads as cleared with the view alongside.
  const viewParams = useMemo(
    () => ({ view: activeTable, groupBy, ...group }),
    [activeTable, group, groupBy],
  );

  const fileGroupFilter = useMemo(() => groupFilterFrom(group, groupBy), [group, groupBy]);

  useFilterParams(filters);
  useFilterParams(viewParams);

  const onViewFilesForGroup = nextGroup => {
    setGroup(nextGroup);
    setActiveTable('files');
  };
  const onViewAllFiles = () => {
    setGroup(NO_GROUP);
    setActiveTable('files');
  };
  const onActiveTableToggle = value => {
    if (!value) {
      return;
    }
    setGroup(NO_GROUP);
    setActiveTable(value);
  };

  return (
    <EnvironmentGuard>
      <Stack gap={2.5}>
        <UnmatchedFilesCharts
          filters={filters}
          onFiltersChange={setFilters}
          onGroupByChange={setGroupBy}
          onActiveTableChange={onActiveTableToggle}
          initialFocusPending={!tableQueriesEnabled}
          onInitialFocusComplete={() => setTableQueriesEnabled(true)}
        />

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 1 }}>
            <Typography
              sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.4 }}>
              {activeTable === 'files'
                ? 'Unmatched Files'
                : activeTable === 'matchedApps'
                  ? 'Matched Apps'
                  : 'Unmatched Code Signers'}
            </Typography>
            <ViewByToggle
              value={activeTable}
              options={VIEW_OPTIONS}
              onChange={onActiveTableToggle}
            />
          </Stack>

          {activeTable === 'files' ? (
            <UnmatchedFilesTable
              filters={filters}
              onFiltersChange={setFilters}
              search={search}
              onSearchChange={setSearch}
              queriesEnabled={tableQueriesEnabled}
              groupFilter={fileGroupFilter}
              onClearGroupFilter={() => setGroup(NO_GROUP)}
              onViewFilesForGroup={onViewFilesForGroup}
            />
          ) : activeTable === 'matchedApps' ? (
            <UnmatchedMatchedAppsTable
              filters={filters}
              onFiltersChange={setFilters}
              search={search}
              onSearchChange={setSearch}
              queriesEnabled={tableQueriesEnabled}
              onViewFilesForGroup={onViewFilesForGroup}
              onViewAllFiles={onViewAllFiles}
            />
          ) : (
            <UnmatchedCodeSignersTable
              filters={filters}
              onFiltersChange={setFilters}
              search={search}
              onSearchChange={setSearch}
              groupBy={groupBy}
              onGroupByChange={setGroupBy}
              queriesEnabled={tableQueriesEnabled}
              onViewFilesForGroup={onViewFilesForGroup}
              onViewAllFiles={onViewAllFiles}
            />
          )}
        </Box>
      </Stack>
    </EnvironmentGuard>
  );
};

export default UnmatchedFiles;
